import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Player, GameRole, Penalty } from '../../types';
import { dictionaries } from '../../utils/dictionary';
import { apiService } from '../../services/api';
import {
  PhaseBackground,
  PhaseContainer,
  PhaseMain,
  SectionCard,
  SuccessCard,
  PlayersTable,
  PhaseButton,
  ActionBar,
} from '../../styles/SharedStyles';
import { theme } from '../../styles/theme';
import PhaseHeader from './PhaseHeader';

interface GameResultsProps {
  language: number;
  players: Player[];
  playerRoles: GameRole[];
  judge?: Player;
  gameState: any;
  clubId?: number;
  eventId?: number;
  tournamentId?: number;
  partNumber?: number;
  protocolPlayerIds?: number[]; // Original protocol player IDs for tournament games
  tableNumber: number;
  gameNumber: number;
  onSubmit: () => void;
  onCancel: () => void;
  onTimeoutToggle?: () => void;
}

interface PlayerScore {
  gameBonus: number;
  penalty: number;
  disciplinaryPenalty: number;
}

const WinnerCard = styled(SuccessCard)`
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.xl};
`;

const WinnerTitle = styled.h4`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 0.05em;
`;

const InputGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const InputLabel = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0 ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md} 0 0 ${theme.borderRadius.md};
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid ${theme.colors.border.secondary};
  border-right: none;
  color: ${theme.colors.text.secondary};
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
`;

const StyledInput = styled.input`
  flex: 1;
  padding: 0.75rem ${theme.spacing.md};
  border-radius: 0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0;
  border: 1px solid ${theme.colors.border.secondary};
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.primary};
  font-weight: 500;
  transition: all ${theme.transitions.normal};

  &:focus {
    outline: none;
    border-color: rgba(124, 93, 255, 0.65);
    box-shadow: 0 0 0 3px rgba(124, 93, 255, 0.15);
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  margin-bottom: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.xl};

  /* Custom scrollbar for better UX */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(124, 93, 255, 0.5);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(124, 93, 255, 0.7);
  }
`;

const ScoresTable = styled(PlayersTable)`
  min-width: 100%;
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    min-width: 600px; /* Ensure table has minimum width for proper layout */
    
    thead th {
      padding: 0.75rem 0.75rem;
      font-size: 0.7rem;
    }

    tbody td {
      padding: 0.75rem 0.75rem;
    }
  }
`;

const PlayerRow = styled.tr<{ $selected?: boolean }>`
  cursor: pointer;
  background: ${({ $selected }) =>
    $selected ? 'rgba(124, 93, 255, 0.15)' : 'transparent'};
  position: relative;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'rgba(124, 93, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const PlayerCell = styled.td`
  font-size: 0.9rem;
`;

const PenaltyInputWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 0.25rem;
  align-items: center;
`;

const PenaltySearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid rgba(124, 93, 255, 0.35);
  background: rgba(124, 93, 255, 0.15);
  color: #d6d0ff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(124, 93, 255, 0.25);
    border-color: rgba(124, 93, 255, 0.5);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 20px;
    height: 20px;
    font-size: 0.7rem;
  }
`;

const InlinePenaltySearch = styled.div<{ $openUpward?: boolean }>`
  position: absolute;
  ${({ $openUpward }) => $openUpward ? 'bottom: 100%;' : 'top: 100%;'}
  right: -50px;
  width: 320px;
  ${({ $openUpward }) => $openUpward ? 'margin-bottom: 0.25rem;' : 'margin-top: 0.25rem;'}
  z-index: 1000;
  background: rgba(18, 12, 36, 0.98);
  border: 1px solid rgba(124, 93, 255, 0.35);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 280px;
    right: -30px;
  }
`;

const InlineSearchInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: none;
  border-bottom: 1px solid rgba(124, 93, 255, 0.2);
  background: transparent;
  color: ${theme.colors.text.primary};
  font-size: 0.85rem;
  outline: none;

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:focus {
    border-bottom-color: rgba(124, 93, 255, 0.5);
  }
`;

const InlinePenaltyList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  padding: 0.25rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(124, 93, 255, 0.5);
    border-radius: 3px;
  }
`;

const InlinePenaltyItem = styled.div`
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s ease;
  font-size: 0.8rem;

  &:hover {
    background: rgba(124, 93, 255, 0.15);
  }
`;

const InlinePenaltySection = styled.div`
  font-weight: 700;
  color: rgba(255, 196, 0, 0.9);
  font-size: 0.75rem;
  margin-bottom: 0.15rem;
`;

const InlinePenaltyText = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: 0.8rem;
  margin-bottom: 0.15rem;
`;

const InlinePenaltySize = styled.div`
  color: #ff4d6d;
  font-weight: 600;
  font-size: 0.75rem;
`;

const RoleBadge = styled.span<{ $role: GameRole }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.15rem 0.4rem;
  margin-left: 0.35rem;
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ $role }) => {
    if ($role === 'don') return theme.colors.role.don.bg;
    if ($role === 'black') return theme.colors.role.mafia.bg;
    if ($role === 'sheriff') return theme.colors.role.sheriff.bg;
    return 'transparent';
  }};
  border: 1px solid
    ${({ $role }) => {
      if ($role === 'don') return theme.colors.role.don.border;
      if ($role === 'black') return theme.colors.role.mafia.border;
      if ($role === 'sheriff') return theme.colors.role.sheriff.border;
      return 'transparent';
    }};
  color: ${({ $role }) => {
    if ($role === 'don') return theme.colors.role.don.text;
    if ($role === 'black') return theme.colors.role.mafia.text;
    if ($role === 'sheriff') return theme.colors.role.sheriff.text;
    return theme.colors.text.primary;
  }};
`;

const ScoreInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ScoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid rgba(124, 93, 255, 0.35);
  background: rgba(124, 93, 255, 0.15);
  color: #d6d0ff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(124, 93, 255, 0.25);
    border-color: rgba(124, 93, 255, 0.5);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 20px;
    height: 20px;
    font-size: 0.75rem;
  }
`;

const ScoreInput = styled.input`
  width: 80px;
  padding: 0.5rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.secondary};
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.primary};
  text-align: center;
  font-weight: 600;
  transition: all ${theme.transitions.normal};

  &:focus {
    outline: none;
    border-color: rgba(124, 93, 255, 0.65);
    box-shadow: 0 0 0 3px rgba(124, 93, 255, 0.15);
  }

  /* Контрастный цвет выделения текста */
  &::selection {
    background: rgba(124, 93, 255, 0.8);
    color: #ffffff;
  }

  &::-moz-selection {
    background: rgba(124, 93, 255, 0.8);
    color: #ffffff;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 65px;
    padding: 0.4rem;
    font-size: 0.9rem;
  }
`;

const TotalRow = styled.tr`
  background: rgba(255, 196, 0, 0.12);
  border-top: 2px solid rgba(255, 196, 0, 0.35);
  font-weight: 700;
`;

const CommentsSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const CommentsLabel = styled.label`
  display: block;
  margin-bottom: ${theme.spacing.sm};
  font-weight: 600;
  font-size: 0.95rem;
  color: ${theme.colors.text.primary};
`;

const CommentsTextarea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.secondary};
  background: rgba(255, 255, 255, 0.05);
  color: ${theme.colors.text.primary};
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.6;
  resize: vertical;
  transition: all ${theme.transitions.normal};

  &:focus {
    outline: none;
    border-color: rgba(124, 93, 255, 0.65);
    box-shadow: 0 0 0 3px rgba(124, 93, 255, 0.15);
  }
`;

const PenaltyDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: rgba(18, 12, 36, 0.98);
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.xl};
  z-index: 1000;
`;

const PenaltyItem = styled.button.attrs({ type: 'button' })`
  width: 100%;
  padding: ${theme.spacing.md};
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: transparent;
  color: ${theme.colors.text.primary};
  text-align: left;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  &:hover {
    background: rgba(124, 93, 255, 0.15);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const PenaltyItemSection = styled.span`
  font-weight: 700;
  color: rgba(124, 93, 255, 0.9);
  font-size: 0.85rem;
`;

const PenaltyItemText = styled.span`
  font-size: 0.9rem;
  opacity: 0.9;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const StatsCard = styled(SectionCard)`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const StatsCardHeader = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 700;
  font-size: 0.95rem;
`;

const StatsCardBody = styled.div`
  padding: ${theme.spacing.lg};

  p {
    margin: 0 0 ${theme.spacing.sm};
    font-size: 0.9rem;
    line-height: 1.6;

    strong {
      color: ${theme.colors.text.primary};
      font-weight: 600;
    }
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 3, 12, 0.72);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1050;
`;

const ModalCard = styled.div`
  width: min(520px, 100%);
  background: linear-gradient(
    145deg,
    rgba(22, 14, 42, 0.96),
    rgba(12, 8, 28, 0.96)
  );
  border: 1px solid rgba(124, 93, 255, 0.22);
  border-radius: 26px;
  box-shadow: 0 28px 90px rgba(4, 2, 14, 0.65);
  color: #f5f2ff;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.75rem 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #f5f2ff;
`;

const ModalCloseButton = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  color: rgba(245, 242, 255, 0.5);
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: #f5f2ff;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem 1.75rem;
`;

const ModalInfo = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(245, 242, 255, 0.85);
`;

const ModalFooter = styled.div`
  padding: 0 1.75rem 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const GameResults: React.FC<GameResultsProps> = ({
  language,
  players,
  playerRoles,
  judge,
  gameState,
  clubId,
  eventId,
  tournamentId,
  partNumber,
  protocolPlayerIds,
  tableNumber,
  gameNumber,
  onSubmit,
  onCancel,
  onTimeoutToggle,
}) => {
  const dict = dictionaries[language].ph;
  
  const [playerScores, setPlayerScores] = useState<PlayerScore[]>(
    Array(10).fill({ gameBonus: 0.0, penalty: 0.0, disciplinaryPenalty: 0.0 })
  );
  const [inputValues, setInputValues] = useState<{[key: string]: string}>({});
  const [selectedPlayer, setSelectedPlayer] = useState<number>(0);
  const [activePenaltySearch, setActivePenaltySearch] = useState<number | null>(null);
  const [inlineSearch, setInlineSearch] = useState<string>('');
  const [penalties, setPenalties] = useState<Penalty[]>([]);
  const [inlineFilteredPenalties, setInlineFilteredPenalties] = useState<Penalty[]>([]);
  const [comments, setComments] = useState<string>(dict.juCommentDef);
  const [showCancelGameConfirm, setShowCancelGameConfirm] = useState(false);
  const [judgeInfo, setJudgeInfo] = useState({
    user_id: judge?.user_id || 0,
    name: judge?.nickname || '',
  });
  const [gameDescription, setGameDescription] = useState(
    `${dict.table} ${tableNumber} ${dict.cgname} ${gameNumber}`
  );
  const [judgeSearch, setJudgeSearch] = useState<string>('');
  const [judgeSearchResults, setJudgeSearchResults] = useState<Player[]>([]);
  const [isJudgeSearchActive, setIsJudgeSearchActive] = useState(false);

  const loadPenalties = async () => {
    try {
      const response = await apiService.getPenalties();
      const penaltiesData = response.body || response.success || [];
      
      if (Array.isArray(penaltiesData) && penaltiesData.length > 0) {
        setPenalties(penaltiesData);
      }
    } catch (error) {
      console.error('Failed to load penalties:', error);
    }
  };

  const initializePlayerScores = () => {
    const newScores = players.map((_, index) => {
      const playerNumber = index + 1;
      let gameBonus = 0.0;
      let penalty = 0.0;

      // Check if firstBlood, but only give bonus if not mafia/don (not a self-shot)
      if (getFirstBlood() === playerNumber) {
        const firstBloodRole = playerRoles[index];
        const isMafiaOrDon = firstBloodRole === 'black' || firstBloodRole === 'don';
        
        if (!isMafiaOrDon) {
          if (gameState.bestMove === 2) {
            gameBonus = 0.5;
          } else if (gameState.bestMove === 3) {
            gameBonus = 0.7;
          }
        }
      }

      if (gameState.playersLeavedPenalty.includes(playerNumber)) {
        penalty = 0.8;
      }

      if (gameState.ppkPlayer === playerNumber) {
        penalty = 1.0;
      }

      return {
        gameBonus,
        penalty,
        disciplinaryPenalty: 0.0,
      };
    });

    setPlayerScores(newScores);
  };

  const handleCancelGame = () => {
    setShowCancelGameConfirm(true);
  };

  useEffect(() => {
    loadPenalties();
    initializePlayerScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close inline penalty search if clicked outside
      if (activePenaltySearch !== null) {
        const target = event.target as HTMLElement;
        const isInsideInlineSearch = target.closest('[data-inline-penalty-search]');
        if (!isInsideInlineSearch) {
          setActivePenaltySearch(null);
          setInlineSearch('');
          setInlineFilteredPenalties([]);
        }
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Close inline search
        if (activePenaltySearch !== null) {
          setActivePenaltySearch(null);
          setInlineSearch('');
          setInlineFilteredPenalties([]);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [activePenaltySearch]);

  useEffect(() => {
    const searchJudge = async (term: string) => {
      try {
        const response = await apiService.searchUsers(term, clubId);
        if (response.success) {
          setJudgeSearchResults(response.success);
        }
      } catch (error) {
        console.error('Judge search failed:', error);
      }
    };

    if (judgeSearch.length >= 1 && isJudgeSearchActive) {
      searchJudge(judgeSearch);
    } else {
      setJudgeSearchResults([]);
    }
  }, [judgeSearch, clubId, isJudgeSearchActive]);

  // Inline penalty search filter
  useEffect(() => {
    if (penalties.length > 0 && activePenaltySearch !== null) {
      const playerRole = playerRoles[activePenaltySearch] || 'red';
      const roleNumber = playerRole === 'red' || playerRole === 'sheriff' ? 1 : 2;

      const filtered = penalties.filter((penalty) => {
        const matchesRole = penalty.role === roleNumber;
        const searchLower = inlineSearch.toLowerCase();
        const matchesSearch =
          penalty.text.toLowerCase().includes(searchLower) ||
          penalty.text_ua.toLowerCase().includes(searchLower) ||
          penalty.section.toLowerCase().includes(searchLower);

        return matchesRole && matchesSearch;
      });

      setInlineFilteredPenalties(filtered);
    } else {
      setInlineFilteredPenalties([]);
    }
  }, [inlineSearch, activePenaltySearch, penalties, playerRoles]);

  const getFirstBlood = (): number => {
    return gameState.otstrel && gameState.otstrel.length > 0 ? gameState.otstrel[0] : 0;
  };

  const getWinnerText = (): string => {
    switch (gameState.pobeda) {
      case 'red':
        return dict.ctwon;
      case 'black':
        return dict.mafwon;
      case 'draw':
        return dict.draw;
      default:
        return dict.game_in_progress;
    }
  };

  const handleScoreChange = (playerIndex: number, field: keyof PlayerScore, value: number) => {
    const newScores = [...playerScores];
    newScores[playerIndex] = {
      ...newScores[playerIndex],
      [field]: value,
    };
    setPlayerScores(newScores);
  };

  // Helper to get input key
  const getInputKey = (playerIndex: number, field: keyof PlayerScore): string => {
    return `${playerIndex}-${field}`;
  };

  // Helper to get display value for input
  const getInputValue = (playerIndex: number, field: keyof PlayerScore): string => {
    const key = getInputKey(playerIndex, field);
    if (inputValues[key] !== undefined) {
      return inputValues[key];
    }
    const numValue = playerScores[playerIndex]?.[field] || 0;
    return String(numValue);
  };

  // Helper to set input value
  const setInputValue = (playerIndex: number, field: keyof PlayerScore, value: string) => {
    const key = getInputKey(playerIndex, field);
    setInputValues(prev => ({ ...prev, [key]: value }));
  };

  // Helper to clear input value (revert to number display)
  const clearInputValue = (playerIndex: number, field: keyof PlayerScore) => {
    const key = getInputKey(playerIndex, field);
    setInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[key];
      return newValues;
    });
  };

  // Helper to parse decimal input with both comma and dot support
  const parseDecimalInput = (value: string): number => {
    if (!value || value === '' || value === '.' || value === ',') {
      return 0;
    }
    // Replace comma with dot for parseFloat, handle multiple separators
    const normalized = value.replace(/,/g, '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : Math.max(0, parsed);
  };

  // Handle input change
  const handleInputChange = (playerIndex: number, field: keyof PlayerScore, value: string) => {
    // Allow typing decimal separators
    setInputValue(playerIndex, field, value);
  };

  // Handle input blur (commit the value)
  const handleInputBlur = (playerIndex: number, field: keyof PlayerScore) => {
    const value = getInputValue(playerIndex, field);
    const numValue = parseDecimalInput(value);
    handleScoreChange(playerIndex, field, numValue);
    clearInputValue(playerIndex, field);
  };

  // Helper to increment score by 0.1
  const incrementScore = (playerIndex: number, field: keyof PlayerScore) => {
    const currentValue = playerScores[playerIndex]?.[field] || 0;
    const newValue = Math.round((currentValue + 0.1) * 10) / 10; // Round to 1 decimal place
    handleScoreChange(playerIndex, field, newValue);
    clearInputValue(playerIndex, field);
  };

  // Helper to decrement score by 0.1
  const decrementScore = (playerIndex: number, field: keyof PlayerScore) => {
    const currentValue = playerScores[playerIndex]?.[field] || 0;
    const newValue = Math.max(0, Math.round((currentValue - 0.1) * 10) / 10); // Round and prevent negative
    handleScoreChange(playerIndex, field, newValue);
    clearInputValue(playerIndex, field);
  };

  const getTotalBonus = (): number => {
    return playerScores.reduce((sum, score) => sum + score.gameBonus, 0);
  };

  const getTotalPenalty = (): number => {
    return playerScores.reduce((sum, score) => sum + score.penalty, 0);
  };

  const getTotalDisciplinary = (): number => {
    return playerScores.reduce((sum, score) => sum + score.disciplinaryPenalty, 0);
  };

  const handleInlinePenaltySelect = (playerIndex: number, penalty: Penalty) => {
    const playerNumber = playerIndex + 1;
    const penaltyText = language === 2 ? penalty.text_ua : penalty.text;
    const newComment = `${comments}\n${playerNumber} - п.${penalty.section} (${penaltyText})`;
    setComments(newComment);

    const newScores = [...playerScores];
    newScores[playerIndex] = {
      ...newScores[playerIndex],
      penalty: newScores[playerIndex].penalty + penalty.size,
    };
    setPlayerScores(newScores);

    // Close inline search
    setActivePenaltySearch(null);
    setInlineSearch('');
    setInlineFilteredPenalties([]);
  };

  const handleJudgeSelect = (player: Player) => {
    setJudgeInfo({
      user_id: player.user_id,
      name: player.nickname,
    });
    setJudgeSearch('');
    setJudgeSearchResults([]);
    setIsJudgeSearchActive(false);
  };

  const handleSubmitResults = async () => {
    // Check if token exists before attempting submission
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Your session has expired. Please log in again.');
      //window.location.href = '/';
      return;
    }

    const gameResult: any = {
      eid: eventId,
      table_number: tableNumber,
      session_number: gameNumber,
      description: gameDescription,
      referee_user_id: judgeInfo.user_id,
      result: {
        best_move_value: gameState.vanga || [],
        start_time: gameState.launchTime,
        end_time: gameState.endTime,
        result: gameState.pobeda,
        black_count: gameState.blackCnt,
        red_count: gameState.redCnt,
        players: players.map((p) => p.user_id),
        player_names: gameState.playerNames,
        player_roles: playerRoles,
        killed_first: getFirstBlood(),
        game_bonus: playerScores.map((s) => s.gameBonus),
        penalty: playerScores.map((s) => s.penalty),
        penalty_disciplinary: playerScores.map((s) => s.disciplinaryPenalty),
        best_move: gameState.bestMove,
        reveal_don: gameState.proverkiDon || [],
        reveal_sheriff: gameState.proverkiSheriff || [],
        player_fouls: gameState.playerFauls,
        comment: comments,
        leaves: [],
      },
    };

    // Add tournament fields if this is a tournament game
    if (tournamentId && partNumber !== undefined) {
      gameResult.tournament_id = tournamentId;
      gameResult.part_number = partNumber;
      
      // Calculate is_replacement array
      // For each player, check if they were in the original protocol
      // 0 = original player, 1 = replacement (guests with negative IDs are always replacements)
      const isReplacement = players.map(player => {
        if (protocolPlayerIds && protocolPlayerIds.length > 0) {
          // Guest players (negative user_id) are always replacements
          if (player.user_id <= 0) return 1;
          return protocolPlayerIds.includes(player.user_id) ? 0 : 1;
        }
        return 0; // Default to 0 if no protocol data
      });
      
      gameResult.result.is_replacement = isReplacement;
    }

    const confirmSubmit = window.confirm(dict.swarning);
    if (confirmSubmit) {
      try {
        let response;
        if (clubId) {
          response = await apiService.saveClubGame(clubId, gameResult);
        } else {
          response = await apiService.saveProtocol(gameResult);
        }
        
        // Check if response has error field
        if (response.error) {
          console.error('Server returned error:', response.error);
          alert(`${dict.upfail}\n\n${response.error}`);
          return; // Don't exit results screen
        }
        
        // Success - exit results screen
        onSubmit();
      } catch (error: any) {
        console.error('Failed to submit results:', error);
        
        // Handle different error types
        if (error?.response?.status === 403 || error?.response?.status === 401) {
          alert('Your session has expired. Please log in again.');
          //window.location.href = '/';
          return;
        }
        
        // Handle validation errors (422)
        if (error?.response?.status === 422) {
          const validationError = error?.response?.data?.error 
            || error?.response?.data?.message
            || 'Validation error: please check all required fields are filled correctly';
          
          // Try to extract specific validation errors if available
          const errors = error?.response?.data?.errors;
          let detailedMessage = validationError;
          
          if (errors && typeof errors === 'object') {
            const errorDetails = Object.entries(errors)
              .map(([field, messages]: [string, any]) => {
                const msgs = Array.isArray(messages) ? messages : [messages];
                return `${field}: ${msgs.join(', ')}`;
              })
              .join('\n');
            
            if (errorDetails) {
              detailedMessage += '\n\n' + errorDetails;
            }
          }
          
          alert(`${dict.upfail}\n\n${detailedMessage}`);
          return;
        }
        
        // For network errors or other errors without response
        let errorMessage = error?.response?.data?.error 
          || error?.response?.data?.message
          || error?.message;
        
        // If it's a generic network error, provide more helpful message
        if (errorMessage === 'Network Error' || !error?.response) {
          errorMessage = 'Failed to submit results. Please check:\n' +
            '- All required fields are filled correctly\n' +
            '- Your internet connection\n';
        }
        
        alert(`${dict.upfail}\n\n${errorMessage}`);
        // Don't call onSubmit() - stay in results screen
      }
    }
  };

  const canSubmit = (): boolean => {
    return judgeInfo.user_id > 0 && gameDescription.length >= 3 && comments.length >= 3;
  };

  return (
    <PhaseBackground $variant="day">
      <PhaseContainer>
        <PhaseHeader
          title={dict.resph}
          variant="day"
          language={language}
          onTimeoutToggle={() => {}}
          hideTimer={true}
          actions={[
            {
              id: 'submit',
              label: dict.upnow,
              onClick: handleSubmitResults,
              disabled: !canSubmit(),
              variant: 'primary',
            },
          ]}
        />

        <PhaseMain>
          <WinnerCard>
            <WinnerTitle>{getWinnerText()}</WinnerTitle>
          </WinnerCard>

          {clubId && (
            <div style={{ marginBottom: theme.spacing.xl }}>
              <div style={{ position: 'relative', marginBottom: theme.spacing.md }}>
                <InputGroup>
                <InputLabel>{dict.judge_label}</InputLabel>
                  <StyledInput
                    type="text"
                    value={isJudgeSearchActive ? judgeSearch : judgeInfo.name}
                    onChange={(e) => {
                      setJudgeSearch(e.target.value);
                      setIsJudgeSearchActive(true);
                    }}
                    onFocus={() => setIsJudgeSearchActive(true)}
                    onBlur={() => {
                      // Delay to allow click on search results
                      setTimeout(() => setIsJudgeSearchActive(false), 200);
                    }}
                    placeholder={dict.search_judge}
                  />
                </InputGroup>
                {judgeSearchResults.length > 0 && isJudgeSearchActive && (
                  <PenaltyDropdown>
                    {judgeSearchResults.slice(0, 10).map((player) => (
                      <PenaltyItem
                        key={player.user_id}
                        onClick={() => handleJudgeSelect(player)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <img
                            src={player.avatar}
                            alt={dict.player_avatar_alt.replace(
                              '{0}',
                              player.nickname
                            )}
                            style={{ width: '30px', height: '30px', borderRadius: '50%' }}
                          />
                          <div>
                            <PenaltyItemSection>{player.nickname}</PenaltyItemSection>
                            {player.club_name && (
                              <PenaltyItemText>{player.club_name}</PenaltyItemText>
                            )}
                          </div>
                        </div>
                      </PenaltyItem>
                    ))}
                  </PenaltyDropdown>
                )}
              </div>
              <InputGroup>
                <InputLabel>{dict.description_label}</InputLabel>
                <StyledInput
                  type="text"
                  value={gameDescription}
                  onChange={(e) => setGameDescription(e.target.value)}
                  placeholder={dict.game_description}
                />
              </InputGroup>
            </div>
          )}

          <TableWrapper>
            <ScoresTable>
              <thead>
                <tr>
                  <th>{dict.player}</th>
                  <th>{dict.g_a}</th>
                  <th>{dict.g_p}</th>
                  <th>{dict.g_dp}</th>
                </tr>
              </thead>
              <tbody>
                {players.map((player, index) => (
                  <PlayerRow
                    key={player.user_id}
                    $selected={selectedPlayer === index}
                    onClick={() => setSelectedPlayer(index)}
                  >
                    <PlayerCell>
                      {index + 1}) {player.nickname}
                      {playerRoles[index] !== 'red' && (
                        <RoleBadge $role={playerRoles[index]}>
                          {playerRoles[index] === 'black' && 'M'}
                          {playerRoles[index] === 'don' && 'D'}
                          {playerRoles[index] === 'sheriff' && 'S'}
                        </RoleBadge>
                      )}
                      {getFirstBlood() === index + 1 && 
                       playerRoles[index] !== 'black' && 
                       playerRoles[index] !== 'don' && 
                       gameState.bestMove === 2 && ' 💀²'}
                      {getFirstBlood() === index + 1 && 
                       playerRoles[index] !== 'black' && 
                       playerRoles[index] !== 'don' && 
                       gameState.bestMove === 3 && ' 💀³'}
                    </PlayerCell>
                    <td>
                      <ScoreInputWrapper>
                        <ScoreButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            decrementScore(index, 'gameBonus');
                          }}
                          title={dict.decrease || 'Decrease'}
                        >
                          −
                        </ScoreButton>
                        <ScoreInput
                          type="text"
                          inputMode="decimal"
                          value={getInputValue(index, 'gameBonus')}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => handleInputChange(index, 'gameBonus', e.target.value)}
                          onBlur={() => handleInputBlur(index, 'gameBonus')}
                        />
                        <ScoreButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            incrementScore(index, 'gameBonus');
                          }}
                          title={dict.increase || 'Increase'}
                        >
                          +
                        </ScoreButton>
                      </ScoreInputWrapper>
                    </td>
                    <td onClick={() => setSelectedPlayer(index)}>
                      <PenaltyInputWrapper data-inline-penalty-search>
                        <ScoreButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            decrementScore(index, 'penalty');
                          }}
                          title={dict.decrease || 'Decrease'}
                        >
                          −
                        </ScoreButton>
                        <ScoreInput
                          type="text"
                          inputMode="decimal"
                          value={getInputValue(index, 'penalty')}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => handleInputChange(index, 'penalty', e.target.value)}
                          onBlur={() => handleInputBlur(index, 'penalty')}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <ScoreButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            incrementScore(index, 'penalty');
                          }}
                          title={dict.increase || 'Increase'}
                        >
                          +
                        </ScoreButton>
                        <PenaltySearchButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActivePenaltySearch(activePenaltySearch === index ? null : index);
                            setInlineSearch('');
                          }}
                          title={dict.search_penalties}
                        >
                          🔍
                        </PenaltySearchButton>
                        {activePenaltySearch === index && (
                          <InlinePenaltySearch $openUpward={index >= 7}>
                            <InlineSearchInput
                              type="text"
                              placeholder={dict.search_penalties}
                              value={inlineSearch}
                              onChange={(e) => setInlineSearch(e.target.value)}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                            {inlineFilteredPenalties.length > 0 && (
                              <InlinePenaltyList>
                                {inlineFilteredPenalties.map((penalty, pIndex) => (
                                  <InlinePenaltyItem
                                    key={pIndex}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleInlinePenaltySelect(index, penalty);
                                    }}
                                  >
                                    <InlinePenaltySection>
                                      {dict.penalty_section.replace('{0}', String(penalty.section))}
                                    </InlinePenaltySection>
                                    <InlinePenaltyText>
                                      {language === 2 ? penalty.text_ua : penalty.text}
                                    </InlinePenaltyText>
                                    <InlinePenaltySize>
                                      {dict.penalty_value.replace('{0}', String(penalty.size))}
                                    </InlinePenaltySize>
                                  </InlinePenaltyItem>
                                ))}
                              </InlinePenaltyList>
                            )}
                          </InlinePenaltySearch>
                        )}
                      </PenaltyInputWrapper>
                    </td>
                    <td>
                      <ScoreInputWrapper>
                        <ScoreButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            decrementScore(index, 'disciplinaryPenalty');
                          }}
                          title={dict.decrease || 'Decrease'}
                        >
                          −
                        </ScoreButton>
                        <ScoreInput
                          type="text"
                          inputMode="decimal"
                          value={getInputValue(index, 'disciplinaryPenalty')}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => handleInputChange(index, 'disciplinaryPenalty', e.target.value)}
                          onBlur={() => handleInputBlur(index, 'disciplinaryPenalty')}
                        />
                        <ScoreButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            incrementScore(index, 'disciplinaryPenalty');
                          }}
                          title={dict.increase || 'Increase'}
                        >
                          +
                        </ScoreButton>
                      </ScoreInputWrapper>
                    </td>
                  </PlayerRow>
                ))}
                <TotalRow>
                  <td style={{ textAlign: 'right' }}>
                    <strong>{dict.total}:</strong>
                  </td>
                  <td>
                    <strong>{getTotalBonus().toFixed(1)}</strong>
                  </td>
                  <td>
                    <strong>{getTotalPenalty().toFixed(1)}</strong>
                  </td>
                  <td>
                    <strong>{getTotalDisciplinary().toFixed(1)}</strong>
                  </td>
                </TotalRow>
              </tbody>
            </ScoresTable>
          </TableWrapper>

          <CommentsSection>
            <CommentsLabel>{dict.juComment}</CommentsLabel>
            <CommentsTextarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder={dict.juComment + '...'}
            />
          </CommentsSection>

          <StatsGrid>
            <StatsCard>
              <StatsCardHeader>{dict.game_statistics}</StatsCardHeader>
              <StatsCardBody>
                <p>
                  <strong>{dict.fblood}:</strong>{' '}
                  {getFirstBlood() > 0
                    ? `${getFirstBlood()}. ${players[getFirstBlood() - 1]?.nickname}`
                    : dict.none}
                </p>
                <p>
                  <strong>{dict.lh}:</strong> {gameState.vanga?.join(', ') || dict.na}
                </p>
                <p>
                  <strong>{dict.lorder}:</strong>{' '}
                  {gameState.playersLeaved?.join(', ') || dict.none}
                </p>
                <p>
                  <strong>{dict.otstr}:</strong> {gameState.otstrel?.join(', ') || dict.none}
                </p>
                <p>
                  <strong>{dict.donchk}:</strong>{' '}
                  {gameState.proverkiDon?.join(', ') || dict.none}
                </p>
                <p>
                  <strong>{dict.shchk}:</strong>{' '}
                  {gameState.proverkiSheriff?.join(', ') || dict.none}
                </p>
              </StatsCardBody>
            </StatsCard>
            <StatsCard>
              <StatsCardHeader>{dict.game_timing}</StatsCardHeader>
              <StatsCardBody>
                <p>
                  <strong>{dict.start_time}:</strong> {gameState.launchTime}
                </p>
                <p>
                  <strong>{dict.end_time}:</strong> {gameState.endTime}
                </p>
                <p>
                  <strong>{dict.nights_count}:</strong> {gameState.nightCount}
                </p>
              </StatsCardBody>
            </StatsCard>
          </StatsGrid>

          <ActionBar>
            <PhaseButton $variant="secondary" onClick={handleCancelGame}>
              {dict.cancelgame}
            </PhaseButton>
          </ActionBar>
        </PhaseMain>

        {showCancelGameConfirm && (
          <ModalOverlay>
            <ModalCard>
              <ModalHeader>
                <ModalTitle>⚠️ {dict.confirm_cancel_game}</ModalTitle>
                <ModalCloseButton onClick={() => setShowCancelGameConfirm(false)}>
                  ×
                </ModalCloseButton>
              </ModalHeader>

              <ModalBody>
                <ModalInfo>
                  {dict.confirm_cancel_game_message}
                </ModalInfo>
              </ModalBody>

              <ModalFooter>
                <PhaseButton 
                  $variant="secondary" 
                  onClick={() => setShowCancelGameConfirm(false)}
                >
                  {dict.cncl}
                </PhaseButton>
                <PhaseButton 
                  $variant="danger" 
                  onClick={() => {
                    setShowCancelGameConfirm(false);
                    onCancel();
                  }}
                >
                  {dict.yes_cancel}
                </PhaseButton>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        )}
      </PhaseContainer>
    </PhaseBackground>
  );
};

export default GameResults;
